import { useEffect, useRef, useState, type DragEvent } from 'react';
import { ContextMenu } from 'radix-ui';
import { ADJUSTMENTS } from '../../adjustments';
import { BLEND_MODE_GROUPS, BLEND_MODE_LABELS } from '../../core/blendModes';
import { hasActiveEffects } from '../../core/layerStyles';
import {
  countLayers,
  findLayerById,
  isGroup,
  isSurfaceLayer,
  locateLayer,
  type InsertTarget,
} from '../../core/layerTree';
import type { BlendMode, Layer } from '../../core/types';
import { getEngine } from '../../engine/engine';
import {
  addLayer,
  beginMetaTransaction,
  commitMetaTransaction,
  deleteLayer,
  duplicateLayer,
  groupActiveLayer,
  mergeDown,
  moveLayerTo,
  patchLayer,
  patchLayerTransient,
  setActiveLayer,
  toggleClippingMask,
  toggleGroupExpanded,
  ungroupActiveLayer,
} from '../../state/actions';
import {
  addLayerMask,
  removeLayerMask,
  selectLayerTarget,
  selectMaskTarget,
  toggleLayerMaskEnabled,
  toggleLayerMaskLinked,
  toggleLayerMaskLocked,
  toggleMaskView,
} from '../../state/maskActions';
import { commandChord } from '../../state/keymap';
import { editTextLayer } from '../../state/textActions';
import { getActiveDocument, getHistoryView, useAppStore } from '../../state/store';
import { Icon, Select } from '../components/controls';
import {
  TRANSFORM_MODES,
  QUICK_TRANSFORMS,
  applyQuickTransform,
  beginTransform,
} from '../transformMenu';
import { Panel } from './Panel';

function layerCanTransform(layer: Layer): boolean {
  return isSurfaceLayer(layer) && !layer.locked && !layer.lockPosition;
}

const BLEND_OPTIONS = BLEND_MODE_GROUPS.flat().map((mode) => ({
  value: mode,
  label: BLEND_MODE_LABELS[mode],
}));

const INDENT_PX = 12;
const FOLDER_ICON =
  'M3 7h6l2 2h10v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z';
const CLIP_ICON = 'M4 4v8h8V4H4zM8 4v4';

type DropZone = 'above' | 'below' | 'inside';

function dropZoneFromEvent(event: DragEvent, expandedGroup: boolean): DropZone {
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  const y = event.clientY - rect.top;
  const h = rect.height;
  if (expandedGroup) {
    if (y < h * 0.25) return 'above';
    if (y > h * 0.75) return 'below';
    return 'inside';
  }
  return y < h * 0.5 ? 'above' : 'below';
}

function insertTargetForDrop(
  zone: DropZone,
  layer: Layer,
  parentId: string | null,
  siblingIndex: number,
): InsertTarget {
  if (zone === 'inside' && isGroup(layer)) {
    return { parentId: layer.id, index: layer.children.length };
  }
  if (zone === 'above') return { parentId, index: siblingIndex + 1 };
  return { parentId, index: siblingIndex };
}

function LayerThumbnail({ layerId, revision }: { layerId: string; revision: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const doc = getActiveDocument(useAppStore.getState());
    if (!canvas || !doc) return;
    const engine = getEngine();
    if (!engine.available) return;

    const image = engine.layerThumbnail(doc, layerId, 30);
    if (!image) return;
    canvas.width = image.width;
    canvas.height = image.height;
    canvas.getContext('2d')?.putImageData(image, 0, 0);
  }, [layerId, revision]);

  return <canvas className="layer__thumb" ref={canvasRef} />;
}

/**
 * Mask thumbnail with the same click language as Photoshop: click edits the
 * mask, Alt+click looks at it in grey, Shift+click switches it off.
 */
function MaskThumbnail({
  layer,
  revision,
  active,
}: {
  layer: Layer;
  revision: number;
  active: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const doc = getActiveDocument(useAppStore.getState());
    if (!canvas || !doc) return;
    const engine = getEngine();
    if (!engine.available) return;

    const image = engine.maskThumbnail(doc, layer.id, 30);
    if (!image) return;
    canvas.width = image.width;
    canvas.height = image.height;
    canvas.getContext('2d')?.putImageData(image, 0, 0);
  }, [layer.id, revision]);

  const classes = [
    'layer__thumb',
    'layer__thumb--mask',
    active ? 'layer__thumb--target' : '',
    layer.maskEnabled ? '' : 'layer__thumb--off',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className="layer__mask">
      <canvas
        className={classes}
        ref={canvasRef}
        title="Click to paint on the mask, Alt+click to view it in grey, Alt+Shift+click as a red overlay, Shift+click to disable"
        onClick={(event) => {
          event.stopPropagation();
          if (event.altKey) toggleMaskView(layer.id, event.shiftKey ? 'overlay' : 'grey');
          else if (event.shiftKey) toggleLayerMaskEnabled(layer.id);
          else selectMaskTarget(layer.id);
        }}
      />
      <button
        type="button"
        className={`layer__link${layer.maskLinked ? ' layer__link--on' : ''}`}
        title={layer.maskLinked ? 'Unlink mask from layer' : 'Link mask to layer'}
        onClick={(event) => {
          event.stopPropagation();
          toggleLayerMaskLinked(layer.id);
        }}
      >
        <Icon size={10} path="M9 15l6-6M8 8H6a4 4 0 000 8h2M16 8h2a4 4 0 010 8h-2" />
      </button>
      <button
        type="button"
        className={`layer__link${layer.lockMask ? ' layer__link--on' : ''}`}
        title={layer.lockMask ? 'Unlock layer mask' : 'Lock layer mask'}
        onClick={(event) => {
          event.stopPropagation();
          toggleLayerMaskLocked(layer.id);
        }}
      >
        <Icon
          size={10}
          path="M6 10V8a6 6 0 1112 0v2h1a1 1 0 011 1v8a1 1 0 01-1 1H5a1 1 0 01-1-1v-8a1 1 0 011-1h1z"
        />
      </button>
    </span>
  );
}

interface LayerRowProps {
  layer: Layer;
  depth: number;
  parentId: string | null;
  siblingIndex: number;
  active: boolean;
  revision: number;
  dragLayerIdRef: { current: string | null };
  onDropTarget: (layerId: string, target: InsertTarget) => void;
}

function LayerRow({
  layer,
  depth,
  parentId,
  siblingIndex,
  active,
  revision,
  dragLayerIdRef,
  onDropTarget,
}: LayerRowProps) {
  const [renaming, setRenaming] = useState(false);
  const [dropZone, setDropZone] = useState<DropZone | null>(null);
  const maskTarget = useAppStore((state) => state.paintTarget === 'mask');
  const expandedGroup = isGroup(layer) && layer.expanded;
  const clipIndent = layer.clipping ? INDENT_PX : 0;
  const hasLock = layer.locked || layer.lockPixels || layer.lockPosition || layer.lockMask;

  const dragClass =
    dropZone === 'above'
      ? ' layer--drag-above'
      : dropZone === 'below'
        ? ' layer--drag-below'
        : dropZone === 'inside'
          ? ' layer--drag-inside'
          : '';
  const canTransform = layerCanTransform(layer);

  return (
    <ContextMenu.Root
      onOpenChange={(open) => {
        if (open) setActiveLayer(layer.id);
      }}
    >
      <ContextMenu.Trigger asChild>
        <div
          className={`layer${active ? ' layer--active' : ''}${dragClass}`}
          style={{ paddingLeft: 8 + depth * INDENT_PX + clipIndent }}
          draggable
          onDragStart={() => {
            dragLayerIdRef.current = layer.id;
          }}
          onDragEnd={() => {
            dragLayerIdRef.current = null;
            setDropZone(null);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setDropZone(dropZoneFromEvent(event, expandedGroup));
          }}
          onDragLeave={() => setDropZone(null)}
          onDrop={(event) => {
            event.preventDefault();
            const zone = dropZoneFromEvent(event, expandedGroup);
            setDropZone(null);
            const draggedId = dragLayerIdRef.current;
            dragLayerIdRef.current = null;
            if (!draggedId || draggedId === layer.id) return;
            onDropTarget(draggedId, insertTargetForDrop(zone, layer, parentId, siblingIndex));
          }}
          onPointerDown={() => setActiveLayer(layer.id)}
          onDoubleClick={() => {
            setActiveLayer(layer.id);
            useAppStore.getState().openDialog({ kind: 'layerStyle' });
          }}
        >
      {isGroup(layer) ? (
        <button
          type="button"
          className="layer__disclosure"
          title={layer.expanded ? 'Collapse group' : 'Expand group'}
          onClick={(event) => {
            event.stopPropagation();
            toggleGroupExpanded(layer.id);
          }}
        >
          <Icon
            size={12}
            path={
              layer.expanded
                ? 'M6 9l6 6 6-6'
                : 'M9 6l6 6-6 6'
            }
          />
        </button>
      ) : (
        <span className="layer__disclosure layer__disclosure--spacer" />
      )}

      <button
        type="button"
        className={`layer__visibility${layer.visible ? ' layer__visibility--on' : ''}`}
        title={layer.visible ? 'Hide layer' : 'Show layer'}
        onClick={(event) => {
          event.stopPropagation();
          patchLayer(layer.id, { visible: !layer.visible }, 'Layer Visibility');
        }}
      >
        <Icon
          size={14}
          path={
            layer.visible
              ? 'M2 12s4-6 10-6 10 6 10 6-4 6-10 6-10-6-10-6zM12 9.5a2.5 2.5 0 100 5 2.5 2.5 0 000-5z'
              : 'M4 4l16 16M2 12s4-6 10-6c2 0 3.7.7 5 1.6M22 12s-4 6-10 6c-2 0-3.7-.7-5-1.6'
          }
        />
      </button>

      {isGroup(layer) ? (
        <span className="layer__thumb layer__thumb--icon" aria-hidden>
          <Icon size={16} path={FOLDER_ICON} />
        </span>
      ) : isSurfaceLayer(layer) ? (
        <span
          className={
            active && !maskTarget ? 'layer__thumb-wrap layer__thumb-wrap--target' : 'layer__thumb-wrap'
          }
          onClick={(event) => {
            event.stopPropagation();
            selectLayerTarget(layer.id);
          }}
          onDoubleClick={(event) => {
            event.stopPropagation();
            if (layer.kind === 'text') {
              editTextLayer(layer.id);
              return;
            }
            setActiveLayer(layer.id);
            useAppStore.getState().openDialog({ kind: 'layerStyle' });
          }}
        >
          <LayerThumbnail layerId={layer.id} revision={revision} />
        </span>
      ) : (
        <span className="layer__thumb layer__thumb--empty" />
      )}

      {layer.hasMask && (
        <MaskThumbnail layer={layer} revision={revision} active={active && maskTarget} />
      )}

      {layer.clipping && (
        <span className="layer__clip" title="Clipped to layer below">
          <Icon size={10} path={CLIP_ICON} />
        </span>
      )}

      {renaming ? (
        <input
          className="layer__name-input"
          autoFocus
          defaultValue={layer.name}
          onBlur={(event) => {
            setRenaming(false);
            const name = event.target.value.trim();
            if (name && name !== layer.name) patchLayer(layer.id, { name }, 'Rename Layer');
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.currentTarget.blur();
            if (event.key === 'Escape') setRenaming(false);
          }}
        />
      ) : (
        <span
          className="layer__name"
          title="Double-click to rename"
          onDoubleClick={(event) => {
            event.stopPropagation();
            setRenaming(true);
          }}
        >
          {layer.name}
        </span>
      )}

      {/* Empty flex space: double-click opens Blending Options, not rename. */}
      <span
        className="layer__style-hit"
        title="Double-click for Blending Options"
        onDoubleClick={(event) => {
          event.stopPropagation();
          setActiveLayer(layer.id);
          useAppStore.getState().openDialog({ kind: 'layerStyle' });
        }}
      />

      {layer.kind === 'adjustment' && (
        <span className="layer__badge">{ADJUSTMENTS[layer.adjustment.id].label}</span>
      )}
      {layer.kind === 'fill' && (
        <span className="layer__badge">
          {layer.fill.kind === 'solid'
            ? 'Color'
            : layer.fill.kind === 'gradient'
              ? 'Gradient'
              : 'Pattern'}
        </span>
      )}
      {layer.kind === 'text' && (
        <span
          className="layer__badge"
          title="Double-click to edit text"
          onDoubleClick={(event) => {
            event.stopPropagation();
            editTextLayer(layer.id);
          }}
        >
          T
        </span>
      )}
      {layer.kind === 'shape' && <span className="layer__badge">Shape</span>}
      <button
        type="button"
        className={`layer__fx${hasActiveEffects(layer.effects) ? ' layer__fx--on' : ''}`}
        title="Blending Options / Layer Style"
        onClick={(event) => {
          event.stopPropagation();
          setActiveLayer(layer.id);
          useAppStore.getState().openDialog({ kind: 'layerStyle' });
        }}
      >
        fx
      </button>
      {hasLock && (
        <span
          className="layer__badge layer__badge--lock"
          title={[
            layer.locked && 'All',
            layer.lockPixels && 'Pixels',
            layer.lockPosition && 'Position',
            layer.lockMask && 'Mask',
          ]
            .filter(Boolean)
            .join(', ')}
        >
          <Icon size={10} path="M6 10V8a6 6 0 1112 0v2h1a1 1 0 011 1v8a1 1 0 01-1 1H5a1 1 0 01-1-1v-8a1 1 0 011-1h1z" />
        </span>
      )}
        </div>
      </ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content className="menu-content" collisionPadding={8}>
          <ContextMenu.Item
            className="menu-item"
            disabled={!canTransform}
            onSelect={() => beginTransform('free')}
          >
            <span>Free Transform</span>
            <span className="menu-item__shortcut">{commandChord('edit.transform')}</span>
          </ContextMenu.Item>
          <ContextMenu.Sub>
            <ContextMenu.SubTrigger className="menu-item" disabled={!canTransform}>
              <span>Transform</span>
              <span className="menu-item__shortcut">▸</span>
            </ContextMenu.SubTrigger>
            <ContextMenu.Portal>
              <ContextMenu.SubContent className="menu-content" sideOffset={4}>
                {TRANSFORM_MODES.map(({ mode, label }) => (
                  <ContextMenu.Item
                    key={mode}
                    className="menu-item"
                    disabled={!canTransform}
                    onSelect={() => beginTransform(mode)}
                  >
                    <span>{label}</span>
                  </ContextMenu.Item>
                ))}
                <ContextMenu.Separator className="menu-separator" />
                {QUICK_TRANSFORMS.map(({ kind, label }) => (
                  <ContextMenu.Item
                    key={kind}
                    className="menu-item"
                    disabled={!canTransform}
                    onSelect={() => applyQuickTransform(kind)}
                  >
                    <span>{label}</span>
                  </ContextMenu.Item>
                ))}
              </ContextMenu.SubContent>
            </ContextMenu.Portal>
          </ContextMenu.Sub>
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}

interface LayerTreeProps {
  layers: readonly Layer[];
  depth: number;
  parentId: string | null;
  activeLayerId: string | null;
  revision: number;
  dragLayerIdRef: { current: string | null };
  onDropTarget: (layerId: string, target: InsertTarget) => void;
}

function LayerTree({
  layers,
  depth,
  parentId,
  activeLayerId,
  revision,
  dragLayerIdRef,
  onDropTarget,
}: LayerTreeProps) {
  // Document order is bottom→top; the panel shows top→bottom like Photoshop.
  // Keep the real siblingIndex for drop targets after reversing for display.
  return layers
    .map((layer, siblingIndex) => ({ layer, siblingIndex }))
    .reverse()
    .map(({ layer, siblingIndex }) => (
      <LayerBranch
        key={layer.id}
        layer={layer}
        depth={depth}
        parentId={parentId}
        siblingIndex={siblingIndex}
        active={layer.id === activeLayerId}
        revision={revision}
        dragLayerIdRef={dragLayerIdRef}
        onDropTarget={onDropTarget}
        activeLayerId={activeLayerId}
      />
    ));
}

interface LayerBranchProps extends LayerRowProps {
  activeLayerId: string | null;
}

function LayerBranch({
  layer,
  depth,
  parentId,
  siblingIndex,
  active,
  revision,
  dragLayerIdRef,
  onDropTarget,
  activeLayerId,
}: LayerBranchProps) {
  return (
    <>
      <LayerRow
        layer={layer}
        depth={depth}
        parentId={parentId}
        siblingIndex={siblingIndex}
        active={active}
        revision={revision}
        dragLayerIdRef={dragLayerIdRef}
        onDropTarget={onDropTarget}
      />
      {isGroup(layer) &&
        layer.expanded &&
        layer.children.length > 0 && (
          <LayerTree
            layers={layer.children}
            depth={depth + 1}
            parentId={layer.id}
            activeLayerId={activeLayerId}
            revision={revision}
            dragLayerIdRef={dragLayerIdRef}
            onDropTarget={onDropTarget}
          />
        )}
    </>
  );
}

export function LayersPanel() {
  const store = useAppStore();
  const doc = getActiveDocument(store);
  const history = getHistoryView(store, store.activeDocumentId);
  const dragLayerIdRef = useRef<string | null>(null);

  const activeLayer = doc ? findLayerById(doc.layers, doc.activeLayerId) : null;
  const activeLocation =
    doc && doc.activeLayerId ? locateLayer(doc.layers, doc.activeLayerId) : null;
  const layerCount = doc ? countLayers(doc.layers) : 0;

  const revision = history.position + history.labels.length * 1000 + store.pixelEpoch * 1e6;

  const handleDropTarget = (draggedId: string, target: InsertTarget) => {
    moveLayerTo(draggedId, target);
  };

  return (
    <Panel
      id="layers"
      title="Layers"
      actions={
        <>
          <button
            type="button"
            className="button button--icon"
            title="New layer (Ctrl+Shift+N)"
            disabled={!doc}
            onClick={addLayer}
          >
            <Icon size={14} path="M12 5v14M5 12h14" />
          </button>
          <button
            type="button"
            className="button button--icon"
            title="Duplicate layer (Ctrl+J)"
            disabled={!activeLayer}
            onClick={() => activeLayer && duplicateLayer(activeLayer.id)}
          >
            <Icon size={14} path="M9 9h11v11H9zM4 4h11v3M4 4v11h3" />
          </button>
          <button
            type="button"
            className="button button--icon"
            title="Group layers"
            disabled={!activeLayer}
            onClick={groupActiveLayer}
          >
            <Icon size={14} path="M4 6h16v12H4zM8 6V4h8v2" />
          </button>
          <button
            type="button"
            className="button button--icon"
            title="Ungroup"
            disabled={!activeLayer || !isGroup(activeLayer)}
            onClick={ungroupActiveLayer}
          >
            <Icon size={14} path="M4 6h16M8 6V4h8v2M4 12h16" />
          </button>
          <button
            type="button"
            className="button button--icon"
            title="Clip to layer below"
            disabled={!activeLayer}
            onClick={() => activeLayer && toggleClippingMask(activeLayer.id)}
          >
            <Icon size={14} path="M4 4v8h8V4H4zM8 4v4" />
          </button>
          <button
            type="button"
            className="button button--icon"
            title={
              activeLayer?.hasMask
                ? 'Delete layer mask'
                : 'Add layer mask (Alt+click hides the selection)'
            }
            disabled={!activeLayer}
            onClick={(event) => {
              if (!activeLayer) return;
              if (activeLayer.hasMask) removeLayerMask(activeLayer.id);
              else addLayerMask(activeLayer.id, event.altKey);
            }}
          >
            <Icon size={14} path="M4 4h16v16H4zM12 4v16" />
          </button>
          <button
            type="button"
            className="button button--icon"
            title="Merge down (Ctrl+E)"
            disabled={!activeLocation || activeLocation.index <= 0}
            onClick={() => activeLayer && mergeDown(activeLayer.id)}
          >
            <Icon size={14} path="M12 4v10m0 0l-4-4m4 4l4-4M4 20h16" />
          </button>
          <button
            type="button"
            className="button button--icon button--danger"
            title="Delete layer"
            disabled={!activeLayer || layerCount <= 1}
            onClick={() => activeLayer && deleteLayer(activeLayer.id)}
          >
            <Icon size={14} path="M5 7h14M9 7V4h6v3M7 7l1 13h8l1-13" />
          </button>
        </>
      }
    >
      <div className="panel__body">
        <div className="field">
          <span className="field__label">Mode</span>
          <Select<BlendMode>
            value={activeLayer?.blendMode ?? 'normal'}
            options={BLEND_OPTIONS}
            onChange={(blendMode) =>
              activeLayer && patchLayer(activeLayer.id, { blendMode }, 'Blend Mode')
            }
          />
        </div>
        <div className="field">
          <span className="field__label">Opacity</span>
          <input
            className="slider"
            type="range"
            min={0}
            max={100}
            value={Math.round((activeLayer?.opacity ?? 1) * 100)}
            disabled={!activeLayer}
            onPointerDown={beginMetaTransaction}
            onPointerUp={() => commitMetaTransaction('Layer Opacity')}
            onChange={(event) =>
              activeLayer &&
              patchLayerTransient(activeLayer.id, {
                opacity: Number.parseInt(event.target.value, 10) / 100,
              })
            }
          />
          <span style={{ width: 34, textAlign: 'right' }}>
            {Math.round((activeLayer?.opacity ?? 1) * 100)}%
          </span>
        </div>
        <div className="layer-locks">
          <button
            type="button"
            className={`button button--icon${activeLayer?.locked ? ' button--active' : ''}`}
            title="Lock all"
            disabled={!activeLayer}
            onClick={() =>
              activeLayer &&
              patchLayer(activeLayer.id, { locked: !activeLayer.locked }, 'Lock Layer')
            }
          >
            <Icon size={14} path="M6 10V8a6 6 0 1112 0v2h1a1 1 0 011 1v8a1 1 0 01-1 1H5a1 1 0 01-1-1v-8a1 1 0 011-1h1z" />
          </button>
          <button
            type="button"
            className={`button button--icon${activeLayer?.lockPixels ? ' button--active' : ''}`}
            title="Lock pixels"
            disabled={!activeLayer}
            onClick={() =>
              activeLayer &&
              patchLayer(activeLayer.id, { lockPixels: !activeLayer.lockPixels }, 'Lock Layer')
            }
          >
            <Icon size={14} path="M4 4h16v16H4zM8 8h8v8H8z" />
          </button>
          <button
            type="button"
            className={`button button--icon${activeLayer?.lockPosition ? ' button--active' : ''}`}
            title="Lock position"
            disabled={!activeLayer}
            onClick={() =>
              activeLayer &&
              patchLayer(
                activeLayer.id,
                { lockPosition: !activeLayer.lockPosition },
                'Lock Layer',
              )
            }
          >
            <Icon size={14} path="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
          </button>
          <button
            type="button"
            className={`button button--icon${activeLayer?.lockMask ? ' button--active' : ''}`}
            title="Lock mask"
            disabled={!activeLayer?.hasMask}
            onClick={() => activeLayer && toggleLayerMaskLocked(activeLayer.id)}
          >
            <Icon size={14} path="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6zM9 9h6v6H9z" />
          </button>
        </div>
      </div>

      <div className="layer-list">
        {doc && (
          <LayerTree
            layers={doc.layers}
            depth={0}
            parentId={null}
            activeLayerId={doc.activeLayerId}
            revision={revision}
            dragLayerIdRef={dragLayerIdRef}
            onDropTarget={handleDropTarget}
          />
        )}
      </div>
    </Panel>
  );
}
